/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './WorkflowViewer';

const WorkflowViewerManager: ComponentManagerFn<'WorkflowViewer'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'WorkflowViewer'>(platform, 'WorkflowViewer'),
    type: 'WorkflowViewer',
    name: 'Workflow Viewer',
    defaultValue,
    props: {
      wfId: {
        label: 'Workflow ID',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
      wfInstId: {
        label: 'Workflow Instance ID',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
    },
    getDisplayText: (item) => item.props.wfId,
    category: 'Others',
    icon: { iconName: 'sitemap' },
    description: `**Overview**<br/>
The Workflow Viewer is a visualization component designed to display the graphical flowchart of a business process. It renders the actual diagram of a workflow definition and overlays the status of a specific running instance, highlighting current steps, completed tasks, and future paths.
Use this component to give users transparency into long-running processes, such as approval chains, order fulfillment steps, or document review cycles.

**1. Configuration**<br/>
To render the diagram accurately, the component requires two specific pieces of identification data, typically supplied via expressions or variables from the application context.
* **Workflow ID (wfId):** The identifier of the workflow definition (the template or design of the process).
* **Workflow Instance ID (wfInstId):** The identifier of the specific runtime execution of that workflow (the actual "job" being tracked).

**2. Functionality**<br/>
* **Process Visualization:** It draws the BPMN (Business Process Model and Notation) or flowchart diagram associated with the workflow design, including tasks, gateways, and transition lines.
* **Status Annotation:** It visually annotates the diagram to show real-time progress. Typically, this involves:
    * **Completed Steps:** Color-coded (e.g., Green) to show successfully finished tasks.
    * **Current Step:** Highlighting the active task (e.g., Blue or pulsing) to indicate where the process is currently held.
    * **Future Path:** Showing the remaining steps in a neutral or faded style to outline the pending workflow.

**3. Best Use Cases**<br/>
* **Approval Tracking:** Allowing a submitter to see exactly whose desk their request is sitting on (e.g., "Pending Manager Approval" vs. "Pending Finance Review").
* **Order Status:** Providing a visual "track-and-trace" for a customer to see the live progress of their package (Received -> Packed -> Shipped -> Delivered).
* **Audit & Debugging:** Helping administrators understand where a process might be "stuck" or which specific branch a conditional logic split followed during execution.
* **Compliance:** Providing a visual record of the path taken by a specific document or transaction for regulatory auditing.`,
  };
};

export default WorkflowViewerManager;
